import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from './cn'

export interface PageHeaderProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** Page h1. Rendered as font-display text-h2 text-strong. */
  title: ReactNode
  /** One-line description under the title. */
  subtitle?: ReactNode
  /** Optional eyebrow/back-link slot above the title. */
  eyebrow?: ReactNode
  /** Right-aligned actions. Wraps under title on mobile. */
  actions?: ReactNode
  /** Status chip next to the title. */
  badge?: ReactNode
  /** Render on an ink hero band. */
  ink?: boolean
  /** Heading level override. */
  as?: 'h1' | 'h2'
  /**
   * Id rendered ON the heading element (not the header wrapper) so
   * `aria-labelledby` regions keep the heading text as their accessible
   * name (2026-07-08 TCP review: id on <header> made region names verbose).
   */
  titleId?: string
  /** Optional harvest-stable name for the title heading (data-exact-set-name). */
  titleExactName?: string
}

export function PageHeader({
  title,
  subtitle,
  eyebrow,
  actions,
  badge,
  ink = false,
  as = 'h1',
  titleId,
  titleExactName,
  className,
  children,
  ...props
}: PageHeaderProps) {
  const Heading = as

  return (
    <header
      className={cn(
        'flex min-w-0 max-w-full flex-col gap-4 sm:flex-row sm:items-start sm:justify-between',
        ink ? 'text-on-ink' : 'text-ink-body',
        className,
      )}
      {...props}
    >
      <div className="min-w-0 flex-1">
        {eyebrow && (
          <div className={cn('mb-2 text-micro font-semibold uppercase tracking-eyebrow', ink ? 'text-on-ink-soft' : 'text-link')}>
            {eyebrow}
          </div>
        )}
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <Heading
            id={titleId}
            data-exact-set-name={titleExactName}
            className={cn('m-0 min-w-0 font-display text-h2 font-normal leading-tight tracking-normal', ink ? 'text-on-ink' : 'text-strong')}
          >
            {title}
          </Heading>
          {badge && <div className="shrink-0">{badge}</div>}
        </div>
        {subtitle && (
          <p className={cn('mt-2 max-w-3xl text-sm leading-relaxed sm:text-base', ink ? 'text-on-ink-soft' : 'text-ink-secondary')}>
            {subtitle}
          </p>
        )}
        {children}
      </div>
      {actions && (
        <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
          {actions}
        </div>
      )}
    </header>
  )
}
